/**
 * SwiftAffiliate Integration Script
 * 
 * This script manages affiliate-related functionalities, including:
 * - Checking if a customer is registered as an affiliate
 * - Saving the affiliate code in session storage
 * - Creating a dashboard redirect button
 * - Creating a share affiliate link button
 * 
 * The script ensures seamless integration with SwiftAffiliate API.
 */

document.addEventListener("DOMContentLoaded", function () {
    // Define constants from the global swiftAffiliate object (if available)
    const swiftAffiliateBaseUrl = window.swiftAffiliate?.base_url;
    const swiftAffiliateDashboardPath = window.swiftAffiliate?.dashboard_path;
    const swiftAffiliateToken = window.swiftAffiliate?.token;
    const swiftAffiliateWebsiteCode = window.swiftAffiliate?.website_code;
    const customerEmail = window.swiftAffiliate?.customer_email;
    const customerFirstname = window.swiftAffiliate?.customer_firstname;
    const customerLastname = window.swiftAffiliate?.customer_lastname;

    /**
     * Checks if the current customer is registered as an affiliate.
     * @returns {Promise<boolean>} True if the customer is registered, otherwise false.
     */
    async function checkAffiliateRegistration() {
        try {
            const response = await fetch(swiftAffiliateBaseUrl + "/graphql", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + swiftAffiliateToken,
                },
                body: JSON.stringify({
                    query: `
                        query {
                            getBrandsByAffiliateEmail(input: { email: "${customerEmail}" }) {
                                website_code
                                website_name
                            }
                        }
                    `,
                }),
            });

            const data = await response.json();

            if (data.errors) {
                return false;
            }

            const brands = data?.data?.getBrandsByAffiliateEmail || [];
            return brands.some(brand => brand.website_code === swiftAffiliateWebsiteCode);
        } catch (error) {
            return false;
        }
    }

    /**
     * Saves the affiliate code from URL parameters into sessionStorage.
     */
    function saveAffiliateCode() {
        const urlParams = new URLSearchParams(window.location.search);
        const affiliateCode = urlParams.get("affc");

        if (affiliateCode) {
            sessionStorage.setItem('has_affiliate_code', true);
            sessionStorage.setItem('affiliate_code', affiliateCode);
        }

    }

    /**
     * Creates and inserts a dashboard redirect button for affiliates.
     */
    function createRedirectDashboardButton() {
        let attempts = 0;
        const maxAttempts = 5;

        async function tryFindingButton() {
            let addressButton = document.querySelector('a[href="/account/addresses"]') || document.getElementById("address-link");
            
            if (addressButton) {
            
                    try {
                        const response = await fetch(swiftAffiliateBaseUrl + "/graphql", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": "Bearer " + swiftAffiliateToken,
                            },
                            body: JSON.stringify({
                                query: `
                                    mutation {
                                        generateLoginUrl(
                                            input: {
                                                email: "${customerEmail}"
                                                website_code: "${swiftAffiliateWebsiteCode}"
                                                firstname: "${customerFirstname}"
                                                lastname: "${customerLastname}"
                                            }
                                        )
                                    }
                                `,
                            }),
                        });

                        const data = await response.json();
                        const redirectUrl = data?.data?.generateLoginUrl;

                        if (redirectUrl) {
                            const clonedButton = addressButton.cloneNode(true);
                            clonedButton.id = "swift-affiliate-dashboard";
                            clonedButton.textContent = "Affiliate Program";
                            clonedButton.href = redirectUrl;
                            clonedButton.setAttribute("target", "_blank");
                            clonedButton.classList.add("redirect-button");

                            const walletLink = document.getElementById("wallet-link");
                            walletLink.parentNode.insertBefore(clonedButton, walletLink.nextSibling);
                        } else {
                            alert("Failed to retrieve the affiliate dashboard URL.");
                        }
                    } catch (error) {
                        alert("Error redirecting to the affiliate dashboard.");
                    }
                
            } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(tryFindingButton, 1000);
            }
        }
        
        tryFindingButton();
    }

    /**
     * Creates and inserts an affiliate share button.
     */
    async function createShareButton() {
        const productForm = document.querySelector("product-form");
        const paymentButton = document.querySelector('.product-info__add-button');

        if (!customerEmail) return;

        const isRegistered = await checkAffiliateRegistration();
        if (!isRegistered) return;

        if (paymentButton) {
            const buyNowButton = paymentButton.querySelector("button");
            const formContainer = document.querySelector('form input[name="form_type"][value="product"]')?.closest("form");

            if (buyNowButton && formContainer) {
                const shareButton = buyNowButton.cloneNode(true);
                shareButton.id = "swift-affiliate-share-button";
                shareButton.textContent = "Share Affiliate Link";
                shareButton.removeAttribute("disabled");
                shareButton.classList.add(...buyNowButton.classList);

                shareButton.addEventListener("click", async () => {
                    const shopUrl = window.swiftAffiliate?.shop_url;
                    const currentPageUrl = window.location.href.split('?')[0];

                    try {
                        const response = await fetch(swiftAffiliateBaseUrl + "/graphql", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": "Bearer " + swiftAffiliateToken,
                            },
                            body: JSON.stringify({
                                query: `
                                    mutation {
                                        generateAffiliatePromoLink(
                                            input: {
                                                email: "${customerEmail}"
                                                website_code: "${swiftAffiliateWebsiteCode}"
                                                basic_url: "${shopUrl}"
                                                traffic_source: "no_source+1"
                                            }
                                        ) {
                                            affiliate_link_code
                                        }
                                    }
                                `,
                            }),
                        });

                        const data = await response.json();
                        const affiliateCode = data?.data?.generateAffiliatePromoLink?.affiliate_link_code;

                        if (affiliateCode) {
                            const affiliateUrl = `${currentPageUrl}?affc=${encodeURIComponent(affiliateCode)}`;
                            await navigator.clipboard.writeText(affiliateUrl);
                            alert("Affiliate URL copied to clipboard!");
                        } else {
                            alert("Failed to generate affiliate link. Please try again.");
                        }
                    } catch (error) {
                        alert("Error generating affiliate link.");
                    }
                });

                productForm.parentNode.insertBefore(shareButton, productForm.nextSibling);
            }
        }
    }

    createRedirectDashboardButton();
    createShareButton();
    saveAffiliateCode();
});